import { useEffect, useState } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { usePrefs } from '../prefs'
import { getChartColors } from '../theme/chartPalette'
import { TimeRange, TimeRangeValue, defaultTimeRange } from '../components/TimeRange'

// ── Types ────────────────────────────────────────────────────────────────────

interface Summary {
  volume_total: number
  hours_total: number
  avg_output_rate: number
}

interface VolumeTrendPoint {
  period: string
  volume: number
}

interface CategoryBreakdownItem {
  category_code: string
  display_name_en: string
  volume: number
}

interface WorkLogItem {
  id: string
  work_date: string
  category_code: string
  display_name_en: string
  task_id: string | null
  task_name: string
  volume: number | null
  hours: string
  note: string | null
  is_compliant: boolean | null
  source: string
}

interface PerformanceData {
  range: { range: string; from: string; to: string }
  summary: Summary
  volume_trend: VolumeTrendPoint[]
  category_breakdown: CategoryBreakdownItem[]
  recent_work_logs: WorkLogItem[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

async function apiFetch(path: string, opts: RequestInit = {}, token: string | null): Promise<any> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(path, { ...opts, headers })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error?.message || `HTTP ${res.status}`)
  }
  if (res.status === 204) return {}
  return res.json()
}

function buildUrl(tr: TimeRangeValue): string {
  const p = new URLSearchParams({ range: tr.range })
  if (tr.range === 'custom') {
    if (tr.from) p.set('from', tr.from)
    if (tr.to) p.set('to', tr.to)
  }
  return `/api/my-performance?${p}`
}

// ── Main page ────────────────────────────────────────────────────────────────

export function MyPerformancePage() {
  const { t } = useLang()
  const { accessToken } = useAuth()
  const { theme } = usePrefs()

  const [timeRange, setTimeRange] = useState<TimeRangeValue>(defaultTimeRange())
  const [data, setData] = useState<PerformanceData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chartColors = getChartColors(theme)

  useEffect(() => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    apiFetch(buildUrl(timeRange), {}, accessToken)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [timeRange, accessToken])

  const tooltipStyle: React.CSSProperties = {
    background: chartColors.tooltipBg,
    color: chartColors.tooltipText,
    border: '1px solid #ccc',
    borderRadius: 6,
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 16, color: 'var(--text-primary)' }}>{t('myPerfTitle')}</h2>

      {/* Time range selector */}
      <div style={{ marginBottom: 20 }}>
        <TimeRange value={timeRange} onChange={setTimeRange} />
      </div>

      {loading && (
        <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('loading')}</div>
      )}

      {error && (
        <div style={{ color: '#c62828', padding: 16 }}>{error}</div>
      )}

      {data && !loading && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <SummaryCard
              label={t('myPerfVolumeTotal')}
              value={data.summary.volume_total.toLocaleString()}
            />
            <SummaryCard
              label={t('myPerfHoursTotal')}
              value={`${Number(data.summary.hours_total).toFixed(1)} ${t('myPerfHoursUnit')}`}
            />
            <SummaryCard
              label={t('myPerfAvgRate')}
              value={`${Number(data.summary.avg_output_rate).toFixed(2)} ${t('myPerfCountUnit')}`}
            />
          </div>

          {/* Charts row */}
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 24 }}>
            {/* Volume trend line chart */}
            <div style={{ flex: '2 1 400px', minWidth: 300 }}>
              <h3 style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                {t('myPerfVolumeTrend')}
              </h3>
              {data.volume_trend.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('myPerfNoData')}</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.volume_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridColor} />
                    <XAxis
                      dataKey="period"
                      tick={{ fontSize: 11, fill: chartColors.axisColor }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: chartColors.axisColor }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <ReTooltip contentStyle={tooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="volume"
                      stroke={chartColors.categoryColors[0]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Category breakdown pie chart */}
            <div style={{ flex: '1 1 280px', minWidth: 260 }}>
              <h3 style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                {t('myPerfCategoryBreakdown')}
              </h3>
              {data.category_breakdown.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('myPerfNoData')}</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.category_breakdown}
                      dataKey="volume"
                      nameKey="display_name_en"
                      cx="50%"
                      cy="45%"
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {data.category_breakdown.map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={chartColors.categoryColors[idx % chartColors.categoryColors.length]}
                        />
                      ))}
                    </Pie>
                    <ReTooltip
                      contentStyle={tooltipStyle}
                      formatter={(value, _name, props) => {
                        const p = props?.payload as CategoryBreakdownItem | undefined
                        return [Number(value).toLocaleString(), p?.display_name_en ?? ''] as [string, string]
                      }}
                    />
                    <Legend
                      wrapperStyle={{ color: chartColors.legendColor, fontSize: 12 }}
                      formatter={(_value, entry) => {
                        const p = (entry as { payload?: CategoryBreakdownItem })?.payload
                        return p?.display_name_en ?? ''
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Recent work logs */}
          <div>
            <h3 style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('myPerfRecentLogs')}
            </h3>
            {data.recent_work_logs.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('myPerfNoData')}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)', color: 'var(--text-muted)' }}>
                      <th style={thStyle}>日期</th>
                      <th style={thStyle}>分类</th>
                      <th style={thStyle}>任务</th>
                      <th style={thStyle}>处理量</th>
                      <th style={thStyle}>工时</th>
                      <th style={thStyle}>备注</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_work_logs.map((log) => (
                      <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={tdStyle}>{log.work_date}</td>
                        <td style={tdStyle}>{log.display_name_en}</td>
                        <td style={tdStyle}>{log.task_name}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{log.volume ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{log.hours}</td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{log.note ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: '1 1 160px',
        padding: '16px 20px',
        borderRadius: 8,
        border: '1px solid var(--border)',
        background: 'var(--card-bg)',
        minWidth: 140,
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  )
}

// ── Style constants ──────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '6px 10px',
  textAlign: 'left',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  whiteSpace: 'nowrap',
  color: 'var(--text-primary)',
}
